import { useEffect, useRef, useState } from 'react';
import { Screen } from '../components/Chrome.js';
import { Icon } from '../components/Icon.js';
import { MINIGAME_META } from '../race/registry.js';
import { color } from '../theme.js';
import type { MinigameId } from '@minigame-race/contracts';
import type { EloPoint, PerGameStat, PlayerStats } from '../lib/useGame.js';

/**
 * Profile stats: match history, the ELO curve, and per-minigame performance.
 * All data comes from /api/stats. Same layout discipline as RulesScreen — one
 * scroll container on the content column, everything sized with min()/clamp().
 */
export function StatsScreen({
  onBack,
  fetchStats,
}: {
  onBack: () => void;
  fetchStats: () => Promise<PlayerStats | null>;
}) {
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let live = true;
    void fetchStats().then((s) => {
      if (!live) return;
      if (s) { setStats(s); setState('ready'); } else setState('error');
    });
    return () => { live = false; };
  }, [fetchStats]);

  return (
    <Screen brackets="top">
      <div
        style={{
          position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column',
          padding: 'clamp(16px,3.4cqmin,38px)', gap: 'clamp(12px,2cqmin,20px)', minHeight: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <button type="button" onClick={onBack} style={backBtn}>
            <Icon name="chevron-left" size={15} /> BACK
          </button>
          <span style={{ fontWeight: 700, fontSize: 'clamp(15px,3cqmin,22px)', letterSpacing: '.06em' }}>YOUR STATS</span>
        </div>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'flex', justifyContent: 'center' }}>
          <div style={{ width: 'min(100%,640px)', display: 'flex', flexDirection: 'column', gap: 'clamp(16px,2.6cqmin,26px)', paddingBottom: 24 }}>
            {state === 'loading' && <Centered>Loading…</Centered>}
            {state === 'error' && <Centered>Couldn’t load stats. Play a ranked match first, or check your connection.</Centered>}
            {state === 'ready' && stats && (
              <>
                <EloChart points={stats.eloTrend} />
                <PerGameTable rows={stats.perGame} />
                <History rows={stats.history} />
              </>
            )}
          </div>
        </div>
      </div>
    </Screen>
  );
}

const backBtn: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
  border: '1px solid rgba(166,255,46,.25)', borderRadius: 999, background: 'rgba(166,255,46,.05)',
  color: color.limeBright, fontWeight: 700, fontSize: 13, letterSpacing: '.04em', cursor: 'pointer', flexShrink: 0,
};

function Centered({ children }: { children: React.ReactNode }) {
  return <div style={{ textAlign: 'center', color: 'rgba(234,255,234,.55)', fontSize: 14, padding: '40px 12px', lineHeight: 1.5 }}>{children}</div>;
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 11, letterSpacing: '.22em', color: 'rgba(166,255,46,.6)', fontWeight: 600, flexShrink: 0 }}>{children}</span>
      <div style={{ flex: 1, height: 1, background: 'rgba(166,255,46,.12)' }} />
    </div>
  );
}

/** ELO over time, drawn on a canvas (crisp, cheap, no external chart lib). */
function EloChart({ points }: { points: readonly EloPoint[] }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth, h = canvas.clientHeight;
    canvas.width = w * dpr; canvas.height = h * dpr;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);
    if (points.length < 2) {
      ctx.fillStyle = 'rgba(234,255,234,.35)';
      ctx.font = "12px 'JetBrains Mono', monospace";
      ctx.textAlign = 'center';
      ctx.fillText('Play a few ranked matches to see your trend', w / 2, h / 2);
      return;
    }
    const pad = 10;
    const elos = points.map((p) => p.elo);
    const min = Math.min(...elos), max = Math.max(...elos);
    const range = max - min || 1;
    const x = (i: number) => pad + (i / (points.length - 1)) * (w - 2 * pad);
    const y = (e: number) => h - pad - ((e - min) / range) * (h - 2 * pad);

    // area fill
    ctx.beginPath();
    ctx.moveTo(x(0), y(elos[0]!));
    points.forEach((p, i) => ctx.lineTo(x(i), y(p.elo)));
    ctx.lineTo(x(points.length - 1), h - pad);
    ctx.lineTo(x(0), h - pad);
    ctx.closePath();
    const grad = ctx.createLinearGradient(0, pad, 0, h - pad);
    grad.addColorStop(0, 'rgba(166,255,46,.28)');
    grad.addColorStop(1, 'rgba(166,255,46,0)');
    ctx.fillStyle = grad;
    ctx.fill();

    // line
    ctx.beginPath();
    ctx.moveTo(x(0), y(elos[0]!));
    points.forEach((p, i) => ctx.lineTo(x(i), y(p.elo)));
    ctx.strokeStyle = '#a6ff2e';
    ctx.lineWidth = 2;
    ctx.stroke();

    // endpoint dot
    ctx.beginPath();
    ctx.arc(x(points.length - 1), y(elos[elos.length - 1]!), 3.5, 0, Math.PI * 2);
    ctx.fillStyle = '#c6ff5e';
    ctx.fill();
  }, [points]);

  const current = points[points.length - 1]?.elo;
  const first = points[0]?.elo;
  const delta = current !== undefined && first !== undefined ? current - first : 0;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SectionTitle>RATING TREND</SectionTitle>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontFamily: "'JetBrains Mono',monospace" }}>
        <span style={{ fontSize: 26, fontWeight: 800, color: color.lime }}>{current ?? '—'}</span>
        {points.length >= 2 && (
          <span style={{ fontSize: 13, fontWeight: 700, color: delta >= 0 ? color.lime : color.redBright }}>
            {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}
          </span>
        )}
        <span style={{ fontSize: 11, color: 'rgba(234,255,234,.4)' }}>over {points.length} ranked</span>
      </div>
      <canvas ref={ref} style={{ width: '100%', height: 'clamp(90px,26cqmin,150px)', display: 'block' }} />
    </section>
  );
}

function PerGameTable({ rows }: { rows: readonly PerGameStat[] }) {
  if (rows.length === 0) return null;
  const title = (key: string): string => MINIGAME_META[key as MinigameId]?.title ?? key.toUpperCase();
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SectionTitle>BY MINIGAME</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r) => {
          const solveRate = r.played === 0 ? 0 : Math.round((r.solved / r.played) * 100);
          return (
            <div key={r.minigameKey} style={{ border: '1px solid rgba(166,255,46,.12)', borderRadius: 10, padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontWeight: 700, fontSize: 13.5, letterSpacing: '.04em', color: color.limeBright }}>{title(r.minigameKey)}</span>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: 'rgba(234,255,234,.55)' }}>
                  {solveRate}% solved · {r.played} played
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: 'rgba(255,255,255,.06)', overflow: 'hidden' }}>
                <div style={{ width: `${solveRate}%`, height: '100%', background: `linear-gradient(90deg,${color.limeDeep},${color.lime})` }} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function History({ rows }: { rows: readonly PlayerStats['history'][number][] }) {
  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <SectionTitle>RECENT MATCHES</SectionTitle>
      {rows.length === 0 ? (
        <Centered>No matches yet. Go race someone.</Centered>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((m) => {
            const tint = m.outcome === 'win' ? color.lime : m.outcome === 'loss' ? color.redBright : color.amber;
            const label = m.outcome === 'win' ? 'WIN' : m.outcome === 'loss' ? 'LOSS' : 'DRAW';
            return (
              <div key={m.matchId} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: 12, alignItems: 'center', padding: '10px 12px', border: '1px solid rgba(234,255,234,.08)', borderRadius: 10, background: 'rgba(255,255,255,.02)' }}>
                <span style={{ fontWeight: 800, fontSize: 11, letterSpacing: '.1em', color: tint, minWidth: 38 }}>{label}</span>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13 }}>
                  vs {m.opponentName}
                  <span style={{ fontFamily: "'JetBrains Mono',monospace", color: 'rgba(234,255,234,.4)', fontSize: 11, marginLeft: 8 }}>
                    {m.yourPoints}–{m.opponentPoints}
                  </span>
                </span>
                <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12, color: m.eloDelta === null ? 'rgba(234,255,234,.3)' : m.eloDelta >= 0 ? color.lime : color.redBright }}>
                  {m.eloDelta === null ? 'casual' : `${m.eloDelta >= 0 ? '+' : ''}${m.eloDelta}`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
